/**
 * CommentsSection — the full comments area at the bottom of an estimate.
 *
 * Includes a header with comment count, an "Add Comment" button, the
 * threaded comment list, and the comment modal. Handles loading state,
 * fetching, and refreshing after a new comment is posted.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Plus, Loader2 } from 'lucide-react';
import type { Comment } from '../../types';
import { fetchComments } from '../../services/api';
import { CommentThread } from './CommentThread';
import { CommentModal } from './CommentModal';
import './CommentsSection.css';

interface CommentsSectionProps {
  estimateGuid: string;
}

export const CommentsSection: React.FC<CommentsSectionProps> = ({ estimateGuid }) => {
  const { t } = useTranslation('results');
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<{ parentId: number; author: string } | null>(null);

  const sectionRef = useRef<HTMLDivElement>(null);

  const loadComments = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchComments(estimateGuid);
      setComments(data);
    } catch {
      // The thrown message is an English server/network string; the user gets
      // our translated one and the retry button, which is what they can act on.
      setError(t('comments.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [estimateGuid, t]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleAddComment = () => {
    setReplyTo(null);
    setModalOpen(true);
  };

  const handleReply = (parentId: number) => {
    const parent = comments.find(c => c.id === parentId);
    setReplyTo({ parentId, author: parent?.authorName || t('comments.thisComment') });
    setModalOpen(true);
  };

  const handleCommentPosted = () => {
    loadComments(); // Refresh the list
  };

  return (
    <div ref={sectionRef} id="estimate-comments" className="comments-section">
      {/* Header */}
      <div className="comments-header">
        <div className="comments-header-left">
          <h2 className="comments-title">
            <MessageSquare size={22} className="comments-title-icon" />
            {t('comments.title')}
            {comments.length > 0 && (
              <span className="comments-count">{comments.length}</span>
            )}
          </h2>
        </div>
        <button
          type="button"
          className="comments-add-btn"
          onClick={handleAddComment}
        >
          <Plus size={16} />
          {t('comments.add')}
        </button>
      </div>

      {/* Comment list */}
      <div className="comments-body">
        {loading ? (
          <div className="comments-loading">
            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <span>{t('comments.loading')}</span>
          </div>
        ) : error ? (
          <div className="comments-error">
            <p>{error}</p>
            <button
              type="button"
              className="comments-retry-btn"
              onClick={() => { setLoading(true); loadComments(); }}
            >
              {t('comments.retry')}
            </button>
          </div>
        ) : (
          <CommentThread comments={comments} onReply={handleReply} />
        )}
      </div>

      {/* Modal */}
      <CommentModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setReplyTo(null); }}
        estimateGuid={estimateGuid}
        parentId={replyTo?.parentId ?? null}
        parentAuthor={replyTo?.author}
        onSuccess={handleCommentPosted}
      />
    </div>
  );
};
